// src/audio/engine.js
// Central Tone.js audio engine: transport, master bus, shared reverb, EQ.

import * as Tone from 'tone';
import { createReverb } from './effects.js';

/**
 * The AudioEngine owns:
 *  - Tone.Transport (the master timeline)
 *  - A master channel feeding the destination
 *  - A shared convolution reverb that every track can send to
 *
 * Tracks plug in by calling `engine.createTrackChannel()` which returns
 * a Tone.Channel wired into the master + reverb send bus.
 */
export class AudioEngine {
  constructor() {
    this.master = null;
    this.reverb = null;
    this.reverbSend = null;
    this.humanizeAmount = 0.012; // seconds of timing jitter
    this.velocityJitter = 0.08;  // fraction of velocity
  }

  /**
   * Must be called from a user gesture (click) — browsers block audio
   * context starts until then. Idempotent.
   */
  async start() {
    if (this.master) return;
    await Tone.start();

    // Master channel: EQ -> Volume -> Destination
    this.master = new Tone.Channel({ volume: 0 });
    this.master.toDestination();

    // Shared reverb
    this.reverb = await createReverb({
      decay: 4.0,
      preDelay: 0.03,
      wet: 1.0 // wet is on the reverb itself; sends control how much reaches it
    });

    // Reverb is a "send" effect: we tap it through a gain so tracks can
    // modulate how much of their signal flows into the reverb.
    this.reverbSend = new Tone.Gain(1.0);
    this.reverbSend.connect(this.reverb);
    this.reverb.connect(this.master);

    // Configure transport defaults
    Tone.Transport.bpm.value = 100;
    Tone.Transport.loop = false;

    // Guarantee the master is at unity gain after initialization.
    // If anything mutes the master before the user clicks Play
    // (e.g. an untraceable Tone.js v14 edge case with Channel
    // initialization), this ensures the piano keyboard produces
    // audible output from the very first key press. Calling this
    // with duration 0 is an instant ramp — no audible artifact.
    this.unmuteMaster(0);
  }

  /**
   * Create a per-track channel wired into master + reverb send.
   * Returns: { channel, reverbSend } — the track connects its instrument
   * to `channel`, and may connect an explicit send to `reverbSend`.
   */
  createTrackChannel() {
    if (!this.master) throw new Error('AudioEngine not started');
    const channel = new Tone.Channel({
      volume: 0,
      pan: 0
    });
    channel.connect(this.master);

    // Per-track send gain (so the UI can adjust reverb send per track)
    const sendGain = new Tone.Gain(0.25);
    sendGain.connect(this.reverbSend);
    channel.connect(sendGain);

    return { channel, sendGain };
  }

  /**
   * Apply timing jitter to an absolute scheduled time. Returns a number
   * (seconds in AudioContext time).
   */
  humanizeTime(t) {
    if (this.humanizeAmount <= 0) return t;
    const jitter = (Math.random() * 2 - 1) * this.humanizeAmount;
    const sec = Tone.Time(t).toSeconds();
    return sec + jitter;
  }

  /**
   * Apply velocity jitter. Returns 0..1.
   */
  humanizeVelocity(v) {
    if (this.velocityJitter <= 0) return v;
    const jitter = (Math.random() * 2 - 1) * this.velocityJitter;
    return Math.max(0.05, Math.min(1, v + jitter));
  }

  setBpm(bpm) {
    Tone.Transport.bpm.rampTo(bpm, 0.1);
  }

  setHumanize({ timing = null, velocity = null } = {}) {
    if (timing !== null) this.humanizeAmount = Math.max(0, timing);
    if (velocity !== null) this.velocityJitter = Math.max(0, Math.min(0.5, velocity));
  }

  setReverbWet(wet) {
    if (this.reverb) this.reverb.wet.rampTo(wet, 0.1);
  }

  /**
   * Cut master output. Used when stopping playback so any in-flight
   * sampler / synth tails go silent immediately. Without this, the
   * Sampler's release envelope and the FMSynth/PluckSynth tails continue
   * to ring even after the timeline is stopped, because Tone.Transport
   * .stop() doesn't cancel already-scheduled voices.
   */
  muteMaster(duration = 0.02) {
    if (this.master) this.master.volume.rampTo(-60, duration);
  }

  /** Restore master output to 0 dB. */
  unmuteMaster(duration = 0.05) {
    if (this.master) this.master.volume.rampTo(0, duration);
  }
}

export const engine = new AudioEngine();
